import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import Region from "../../../../interfaces/identity/Region";
import Role from "../../../../interfaces/identity/Role";
import { FormData, FormErrors } from "./AddUser";
import { reportTo, teams } from "../../../../interfaces/team/RegionHierarchy";
import { ROLE_IDS, ROLES } from "../../../../Data/SD";
import Waiver from "../../../../interfaces/participant/Waiver";

// Extend Waiver locally to ensure TypeScript knows about familyCode
// (Assuming the backend update now returns this field)
interface WaiverWithFamily extends Waiver {
  familyCode?: string;
}

interface FormDesignProps {
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  handleChange: (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
      | { target: { name: string; value: any; type: string } }
  ) => void;
  handleCancel: () => void;
  roles: Role[];
  regions: Region[];
  reportTo: reportTo[];
  teams: teams[];
  waivers: WaiverWithFamily[];
  errors: FormErrors;
  hideRegionTeamSelection?: boolean;
  hasChanges?: boolean;
  hasDisabledFieldForUpdating?: boolean;
}

const FormDesign: React.FC<FormDesignProps> = (props) => {
  const {
    handleSubmit,
    formData,
    handleChange,
    handleCancel,
    roles,
    regions,
    reportTo,
    teams,
    waivers,
    errors,
    hideRegionTeamSelection,
    hasChanges = true,
    hasDisabledFieldForUpdating = false
  } = props;

  // State for Role Dropdown
  const [roleDropdownOpen, setRoleDropdownOpen] = useState<boolean>(false);
  const [roleSearchTerm, setRoleSearchTerm] = useState<string>("");
  const roleDropdownRef = useRef<HTMLDivElement>(null);

  // State for Region Dropdown
  const [regionDropdownOpen, setRegionDropdownOpen] = useState<boolean>(false);
  const [regionSearchTerm, setRegionSearchTerm] = useState<string>("");
  const regionDropdownRef = useRef<HTMLDivElement>(null);

  // State for Team Dropdown
  const [teamDropdownOpen, setTeamDropdownOpen] = useState<boolean>(false);
  const [teamSearchTerm, setTeamSearchTerm] = useState<string>("");
  const teamDropdownRef = useRef<HTMLDivElement>(null);
  const teamButtonRef = useRef<HTMLButtonElement>(null);
  const [teamDropdownPosition, setTeamDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  // State for Waiver Family Dropdown
  const [waiverDropdownOpen, setWaiverDropdownOpen] = useState<boolean>(false);
  const [waiverSearchTerm, setWaiverSearchTerm] = useState<string>("");
  const waiverDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        roleDropdownRef.current &&
        !roleDropdownRef.current.contains(event.target as Node)
      ) {
        setRoleDropdownOpen(false);
      }
      if (
        regionDropdownRef.current &&
        !regionDropdownRef.current.contains(event.target as Node)
      ) {
        setRegionDropdownOpen(false);
      }
      if (
        waiverDropdownRef.current &&
        !waiverDropdownRef.current.contains(event.target as Node)
      ) {
        setWaiverDropdownOpen(false);
      }
      if (
        teamDropdownRef.current &&
        !teamDropdownRef.current.contains(event.target as Node) &&
        teamButtonRef.current &&
        !teamButtonRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('[data-team-dropdown-portal]')
      ) {
        setTeamDropdownOpen(false);
        setTeamDropdownPosition(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- Role Handlers ---
  const handleSelectAllRoles = useCallback(() => {
    const allRoleIds = roles.map((r) => r.id);
    handleChange({
      target: { name: "roleIds", value: allRoleIds, type: "checkbox-group" },
    });
  }, [roles, handleChange]);

  const handleUnselectAllRoles = useCallback(() => {
    handleChange({
      target: { name: "roleIds", value: [], type: "checkbox-group" },
    });
  }, [handleChange]);

  const toggleRole = useCallback(
    (id: number) => {
      const updated = formData.roleIds.includes(id)
        ? formData.roleIds.filter((rid) => rid !== id)
        : [...formData.roleIds, id];

      handleChange({
        target: { name: "roleIds", value: updated, type: "checkbox-group" },
      });
    },
    [formData.roleIds, handleChange]
  );

  const filteredRoles = roles.filter(
    (role) =>
      role.id !== ROLE_IDS.ADMIN &&
      role.id != ROLE_IDS.OWNER &&
      role.name.toLowerCase().includes(roleSearchTerm.toLowerCase())
  );

  // --- Region Handlers ---
  const handleSelectAllRegions = useCallback(() => {
    const allRegionIds = regions.map((r) => r.regionId);
    handleChange({
      target: {
        name: "regionIds",
        value: allRegionIds,
        type: "checkbox-group",
      },
    });
  }, [regions, handleChange]);

  const handleUnselectAllRegions = useCallback(() => {
    handleChange({
      target: { name: "regionIds", value: [], type: "checkbox-group" },
    });
  }, [handleChange]);

  const toggleRegion = useCallback(
    (id: number) => {
      const updated = formData.regionIds.includes(id)
        ? formData.regionIds.filter((rid) => rid !== id)
        : [...formData.regionIds, id];
      handleChange({
        target: { name: "regionIds", value: updated, type: "checkbox-group" },
      });
      handleChange({
        target: { name: "TeamIds", value: [], type: "checkbox-group" },
      });
    },
    [formData.regionIds, handleChange]
  );

  const filteredRegions = regions.filter((region) =>
    region.regionName.toLowerCase().includes(regionSearchTerm.toLowerCase())
  );

  // --- Team Handlers ---
  const handleSelectAllTeams = useCallback(() => {
    const allTeamIds = teams.map((t) => t.id);
    handleChange({
      target: { name: "TeamIds", value: allTeamIds, type: "checkbox-group" },
    });
  }, [teams, handleChange]);

  const handleUnselectAllTeams = useCallback(() => {
    handleChange({
      target: { name: "TeamIds", value: [], type: "checkbox-group" },
    });
  }, [handleChange]);

  const toggleTeam = useCallback(
    (id: number) => {
      const updated = formData.TeamIds.includes(id)
        ? formData.TeamIds.filter((tid) => tid !== id)
        : [...formData.TeamIds, id];
      handleChange({
        target: { name: "TeamIds", value: updated, type: "checkbox-group" },
      });
    },
    [formData.TeamIds, handleChange]
  );

  const filteredTeams = teams.filter((team) =>
    team.teamName.toLowerCase().includes(teamSearchTerm.toLowerCase())
  );

  // --- WAIVER FAMILY LOGIC START ---

  // 1. Group waivers by their Family Code to display unique options
  const uniqueWaiverFamilies = useMemo(() => {
    const familyMap = new Map<string, { familyName: string, representativeId: number }>();

    waivers.forEach((w) => {
      // Use familyCode if available, otherwise fallback to waiverCode (for backwards compatibility)
      const code = w.familyCode || w.waiverCode; 
      
      if (code && !familyMap.has(code)) {
        familyMap.set(code, {
          familyName: code, 
          representativeId: w.id // We just need ONE valid ID to represent this family for "Checking"
        });
      }
    });

    return Array.from(familyMap.values());
  }, [waivers]);

  // 2. Filter families based on search term
  const filteredWaiverFamilies = uniqueWaiverFamilies.filter((f) => 
    f.familyName.toLowerCase().includes(waiverSearchTerm.toLowerCase())
  );

  // 3. Selection Handler: Handles "Family" level selection
  const toggleWaiverFamilySelection = useCallback((familyCode: string, representativeId: number) => {
    // Find ALL waiver IDs in the system that belong to this family
    // This allows us to catch "Ghost" IDs or multiple IDs for the same family
    const familyIds = waivers
        .filter(w => (w.familyCode === familyCode || w.waiverCode === familyCode))
        .map(w => w.id);

    // Check if ANY of the user's current selections belong to this family
    const isSelected = formData.waiverFamilyIds.some(id => familyIds.includes(id));
    
    let newIds = [...formData.waiverFamilyIds];

    if (isSelected) {
        // UNCHECK: Remove ALL IDs associated with this family
        // This effectively "cleans" the user's profile of conflicting IDs for this family
        newIds = newIds.filter(id => !familyIds.includes(id));
    } else {
        // CHECK: Add the representative ID (The valid one from the current list)
        newIds.push(representativeId);
    }

    handleChange({
      target: { name: "waiverFamilyIds", value: newIds, type: "checkbox-group" }
    });
  }, [formData.waiverFamilyIds, waivers, handleChange]);

  const handleSelectAllWaiverFamilies = useCallback(() => {
    // Select one representative ID from every family
    const allRepIds = uniqueWaiverFamilies.map(f => f.representativeId);
    handleChange({
      target: { name: "waiverFamilyIds", value: allRepIds, type: "checkbox-group" },
    });
  }, [uniqueWaiverFamilies, handleChange]);

  const handleUnselectAllWaiverFamilies = useCallback(() => {
    handleChange({
      target: { name: "waiverFamilyIds", value: [], type: "checkbox-group" },
    });
  }, [handleChange]);

  // Determine the label for the closed dropdown
  const getSelectedWaiverLabel = () => {
    if (formData.waiverFamilyIds.length === 0) return "Select waiver family(s)";
    
    // Find which families are currently active
    const selectedFamilies = uniqueWaiverFamilies
        .filter(f => {
            const familyIds = waivers
                .filter(w => (w.familyCode === f.familyName || w.waiverCode === f.familyName))
                .map(w => w.id);
            // If user has ANY id from this family, we consider the family selected
            return formData.waiverFamilyIds.some(id => familyIds.includes(id));
        })
        .map(f => f.familyName);

    return selectedFamilies.length > 0 ? selectedFamilies.join(", ") : "Select waiver family(s)";
  };

  // --- WAIVER FAMILY LOGIC END ---

  // Calculate dropdown position for Teams dropdown
  const calculateTeamDropdownPosition = useCallback(() => {
    if (teamButtonRef.current) {
      const rect = teamButtonRef.current.getBoundingClientRect();
      setTeamDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, []);

  const handleTeamDropdownToggle = useCallback(() => {
    if (!teamDropdownOpen) {
      calculateTeamDropdownPosition();
      setTeamDropdownOpen(true);
    } else {
      setTeamDropdownOpen(false);
      setTeamDropdownPosition(null);
    }
  }, [teamDropdownOpen, calculateTeamDropdownPosition]);

  // Update position on scroll/resize when dropdown is open
  useEffect(() => {
    if (teamDropdownOpen) {
      const updatePosition = () => {
        calculateTeamDropdownPosition();
      };
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [teamDropdownOpen, calculateTeamDropdownPosition]);

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {/* First Name */}
        <div className="col-span-1">
          <label className="block text-sm font-normal text-black mb-2">
            First Name
          </label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            placeholder="Enter first name"
            className={`w-full px-4 py-2.5 border ${errors.firstName
              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
              : "border-gray-300 focus:border-purple-500 focus:ring-purple-500"
              } rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:ring-1`}
          />
          {errors.firstName && (
            <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
          )}
        </div>

        {/* Last Name */}
        <div className="col-span-1">
          <label className="block text-sm font-normal text-black mb-2">
            Last Name
          </label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            placeholder="Enter last name"
            className={`w-full px-4 py-2.5 border ${errors.lastName
              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
              : "border-gray-300 focus:border-purple-500 focus:ring-purple-500"
              } rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:ring-1`}
          />
          {errors.lastName && (
            <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
          )}
        </div>

        {/* Email */}
        <div className="col-span-1">
          <label className="block text-sm font-normal text-black mb-2">
            Email
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter email"
            disabled={hasDisabledFieldForUpdating}
            className={`w-full px-4 py-2.5 border ${errors.email
              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
              : "border-gray-300 focus:border-purple-500 focus:ring-purple-500"
              } ${hasDisabledFieldForUpdating ? "text-gray-400" : ""} rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:ring-1`}
          />
          {errors.email && (
            <p className="text-red-500 text-xs mt-1">{errors.email}</p>
          )}
        </div>

        {/* Display Name */}
        <div className="col-span-1">
          <label className="block text-sm font-normal text-black mb-2">
            Display Name
          </label>
          <input
            type="text"
            name="displayName"
            value={formData.displayName}
            onChange={handleChange}
            placeholder="Enter display name"
            className={`w-full px-4 py-2.5 border ${errors.displayName
              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
              : "border-gray-300 focus:border-purple-500 focus:ring-purple-500"
              } rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:ring-1`}
          />
          {errors.displayName && (
            <p className="text-red-500 text-xs mt-1">{errors.displayName}</p>
          )}
        </div>

        {/* Waiver Family - UPDATED DROPDOWN */}
        <div className="col-span-1" ref={waiverDropdownRef}>
          <label className="block text-sm font-normal text-black mb-2">Waiver Family</label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setWaiverDropdownOpen(!waiverDropdownOpen)}
              className={`w-full px-4 py-2.5 border ${errors.waiverFamilyIds
                ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-purple-500 focus:ring-purple-500"
              } rounded-lg text-sm text-left flex items-center justify-between focus:outline-none focus:ring-1 bg-white`}
            >
            <span
              className={formData.waiverFamilyIds.length > 0 ? "text-black" : "text-gray-400"}
              style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}
              title={getSelectedWaiverLabel() === "Select waiver family(s)" ? undefined : getSelectedWaiverLabel()}
            >
                {getSelectedWaiverLabel()}
              </span>
              <ChevronDown size={16} className="text-gray-400" />
            </button>

            {waiverDropdownOpen && (
              <div className="absolute z-20 w-full bg-white border border-gray-200 mt-1 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                <div className="p-2 border-b">
                  <input
                    type="text"
                    placeholder="Search waiver families..."
                    value={waiverSearchTerm}
                    onChange={(e) => setWaiverSearchTerm(e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div className="flex items-center justify-between px-3 py-2 border-b text-xs">
                  <button type="button" onClick={handleSelectAllWaiverFamilies} className="text-blue-600 hover:underline">Select All</button>
                  <button type="button" onClick={handleUnselectAllWaiverFamilies} className="text-red-500 hover:underline">Unselect All</button>
                </div>

                {/* Filtered Waiver Families */}
                {filteredWaiverFamilies.length > 0 ? (
                  filteredWaiverFamilies.map((family) => {
                     // Check if ANY of the user's selected IDs belong to this family
                     const familyIds = waivers
                        .filter(w => (w.familyCode === family.familyName || w.waiverCode === family.familyName))
                        .map(w => w.id);
                     const isChecked = formData.waiverFamilyIds.some(id => familyIds.includes(id));

                     return (
                        <label key={family.familyName} className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleWaiverFamilySelection(family.familyName, family.representativeId)}
                            className="mr-2"
                          />
                          <div className="flex items-center">
                            {/* Display Family Name (e.g. OCDD) */}
                            <span>{family.familyName}</span>
                          </div>
                        </label>
                     );
                  })
                ) : (
                  <div className="px-3 py-2 text-gray-400 text-sm">No waiver families found</div>
                )}
              </div>
            )}
          </div>
          {errors.waiverFamilyIds && <p className="text-red-500 text-xs mt-1">{errors.waiverFamilyIds}</p>}
        </div>

        {/* Employee ID */}
        <div className="col-span-1">
          <label className="block text-sm font-normal text-black mb-2">
            Employee ID
          </label>
          <input
            type="text"
            name="employeeId"
            value={formData.employeeId}
            onChange={handleChange}
            placeholder="Enter employee id"
            className={`w-full px-4 py-2.5 border ${errors.employeeId
              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
              : "border-gray-300 focus:border-purple-500 focus:ring-purple-500"
              } rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:ring-1`}
          />
          {errors.employeeId && (
            <p className="text-red-500 text-xs mt-1">{errors.employeeId}</p>
          )}
        </div>


        {/* Phone Number */}
        <div className="col-span-1">
          <label className="block text-sm font-normal text-black mb-2">
            Phone No (10 digits)
          </label>
          <input
            type="tel"
            name="phoneNumber"
            maxLength={10}
            value={formData.phoneNumber}
            onChange={handleChange}
            placeholder="Enter 10 digit phone"
            className={`w-full px-4 py-2.5 border ${errors.phoneNumber
              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
              : "border-gray-300 focus:border-purple-500 focus:ring-purple-500"
              } rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:ring-1`}
          />
          {errors.phoneNumber && (
            <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>
          )}
        </div>

        {/* Role */}
        <div className="col-span-1" ref={roleDropdownRef}>
          <label className="block text-sm font-normal text-black mb-2">
            Role
          </label>
          <div className="relative">
            <button
              type="button"
              disabled={hasDisabledFieldForUpdating}
              onClick={() => setRoleDropdownOpen(!roleDropdownOpen)}
              className={`w-full px-4 py-2.5 border ${errors.roleIds
                ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                } rounded-lg text-sm text-left flex items-center justify-between focus:outline-none focus:ring-1 bg-white`}
            >
            <span
              className={formData?.roleIds?.length > 0 ? hasDisabledFieldForUpdating ? "text-gray-400" : "text-black" : "text-gray-400"}
              style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}
              title={
                formData.roleIds.length > 0
                  ? roles
                      .filter((r) => formData.roleIds.includes(r.id))
                      .map((r) => r.name)
                      .join(", ")
                  : undefined
              }
            >
                {formData.roleIds.length > 0
                  ? roles
                    .filter((r) => formData.roleIds.includes(r.id))
                    .map((r) => r.name)
                    .join(", ")
                  : "Select role(s)"}
              </span>
              <ChevronDown size={16} className="text-gray-400" />
            </button>

            {roleDropdownOpen && (
              <div className="absolute z-20 w-full bg-white border border-gray-200 mt-1 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                <div className="p-2 border-b">
                  <input
                    type="text"
                    placeholder="Search roles..."
                    value={roleSearchTerm}
                    onChange={(e) => setRoleSearchTerm(e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div className="flex items-center justify-between px-3 py-2 border-b text-xs">
                  <button
                    type="button"
                    onClick={handleSelectAllRoles}
                    className="text-blue-600 hover:underline"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={handleUnselectAllRoles}
                    className="text-red-500 hover:underline"
                  >
                    Unselect All
                  </button>
                </div>
                {filteredRoles.length > 0 ? (
                  filteredRoles.map(
                    (role) =>
                      role.name !== ROLES.SCRUM_MASTER &&
                      role.name !== ROLES.DEVELOPER &&
                      role?.name !== ROLES?.ASSESSOR && (
                        <label
                          key={role.id}
                          className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={formData.roleIds.includes(role.id)}
                            onChange={() => toggleRole(role.id)}
                            className="mr-2"
                          />
                          {role.name}
                        </label>
                      )
                  )
                ) : (
                  <div className="px-3 py-2 text-gray-400 text-sm">
                    No roles found
                  </div>
                )}
              </div>
            )}
          </div>
          {errors.roleIds && (
            <p className="text-red-500 text-xs mt-1">{errors.roleIds}</p>
          )}
        </div>


        {/* Region (Conditional) */}
        {!hideRegionTeamSelection && (
          <div className="col-span-1" ref={regionDropdownRef}>
            <label className="block text-sm font-normal text-black mb-2">
              Region
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setRegionDropdownOpen(!regionDropdownOpen)}
                className={`w-full px-4 py-2.5 border ${errors.regionIds
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                  } rounded-lg text-sm text-left flex items-center justify-between focus:outline-none focus:ring-1 bg-white`}
              >
              <span
                className={formData.regionIds.length > 0 ? "text-black" : "text-gray-400"}
                style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}
                title={
                  formData.regionIds.length > 0
                    ? regions
                        .filter((r) => formData.regionIds.includes(r.regionId))
                        .map((r) => r.regionName)
                        .join(", ")
                    : undefined
                }
              >
                  {formData.regionIds.length > 0
                    ? regions
                      .filter((r) => formData.regionIds.includes(r.regionId))
                      .map((r) => r.regionName)
                      .join(", ")
                    : "Select region(s)"}
                </span>
                <ChevronDown size={16} className="text-gray-400" />
              </button>

              {regionDropdownOpen && (
                <div className="absolute z-20 w-full bg-white border border-gray-200 mt-1 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                  <div className="p-2 border-b">
                    <input
                      type="text"
                      placeholder="Search regions..."
                      value={regionSearchTerm}
                      onChange={(e) => setRegionSearchTerm(e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-purple-500"
                    />
                  </div>
                  <div className="flex items-center justify-between px-3 py-2 border-b text-xs">
                    <button
                      type="button"
                      onClick={handleSelectAllRegions}
                      className="text-blue-600 hover:underline"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={handleUnselectAllRegions}
                      className="text-red-500 hover:underline"
                    >
                      Unselect All
                    </button>
                  </div>
                  {filteredRegions.length > 0 ? (
                    filteredRegions.map((region) => (
                      <label
                        key={region.regionId}
                        className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={formData.regionIds.includes(region.regionId)}
                          onChange={() => toggleRegion(region.regionId)}
                          className="mr-2"
                        />
                        {region.regionName}
                      </label>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-gray-400 text-sm">
                      No regions found
                    </div>
                  )}
                </div>
              )}
            </div>
            {errors.regionIds && (
              <p className="text-red-500 text-xs mt-1">{errors.regionIds}</p>
            )}
          </div>
        )}


        {/* Teams (Conditional) */}
        {!hideRegionTeamSelection && (
          <div className="col-span-1" ref={teamDropdownRef}>
            <label className="block text-sm font-normal text-black mb-2">
              Teams
            </label>

            {formData.roleIds.includes(ROLE_IDS.PROGRAM_MANAGER) ? (
              // When Program Manager is selected, show explanatory text instead of dropdown
              <div className="w-full flex justify-between px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-500 bg-gray-50">
                Select teams <ChevronDown size={16} className="text-gray-400" />
              </div>
            ) : (
              <div className="relative">
                <button
                  ref={teamButtonRef}
                  type="button"
                  onClick={handleTeamDropdownToggle}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-left flex items-center justify-between focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 bg-white"
                >
                  <span
                    className={
                      formData.TeamIds.length > 0 ? "text-black" : "text-gray-400"
                    }
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "block",
                    }}
                    title={
                      formData.TeamIds.length > 0
                        ? teams
                            .filter((t) => formData.TeamIds.includes(t.id))
                            .map((t) => t.teamName)
                            .join(", ")
                        : undefined
                    }
                  >
                    {formData.TeamIds.length > 0
                      ? teams
                          .filter((t) => formData.TeamIds.includes(t.id))
                          .map((t) => t.teamName)
                          .join(", ")
                      : "Select teams"}
                  </span>
                  <ChevronDown size={16} className="text-gray-400" />
                </button>

                {teamDropdownOpen &&
                  teamDropdownPosition &&
                  createPortal(
                    <div
                      data-team-dropdown-portal
                      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto"
                      style={{
                        top: `${teamDropdownPosition.top}px`,
                        left: `${teamDropdownPosition.left}px`,
                        width: `${teamDropdownPosition.width}px`,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="p-2 border-b">
                        <input
                          type="text"
                          placeholder="Search teams..."
                          value={teamSearchTerm}
                          onChange={(e) => setTeamSearchTerm(e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-purple-500"
                          autoFocus
                        />
                      </div>
                      <div className="flex items-center justify-between px-3 py-2 border-b text-xs">
                        <button
                          type="button"
                          onClick={handleSelectAllTeams}
                          className="text-blue-600 hover:underline"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={handleUnselectAllTeams}
                          className="text-red-500 hover:underline"
                        >
                          Unselect All
                        </button>
                      </div>
                      {filteredTeams.length > 0 ? (
                        filteredTeams.map((team) => (
                          <label
                            key={team.id}
                            className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                          >
                            <input
                              type="checkbox"
                              checked={formData.TeamIds.includes(team.id)}
                              onChange={() => toggleTeam(team.id)}
                              className="mr-2"
                            />
                            {team.teamName}
                          </label>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-gray-400 text-sm">
                          No teams found
                        </div>
                      )}
                    </div>,
                    document.body
                  )}
              </div>
            )}

            {errors.TeamIds && (
              <p className="text-red-500 text-xs mt-1">{errors.TeamIds}</p>
            )}
          </div>
        )}



        {/* Manager */}
        <div className="col-span-1">
          <label className="block text-sm font-normal text-black mb-2">
            Manager
          </label>
          <div className="relative">
            <select
              name="ManagerId"
              value={formData.ManagerId}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 appearance-none bg-white"
            >
              <option value="">Select Manager</option>
              {reportTo?.map((report) => (
                <option key={report.id} value={report.id}>
                  {report.fullName}{" "}
                  {report.teamNames.length
                    ? `(${report.teamNames.join(", ")})`
                    : ""}
                </option>
              ))}
            </select>
            <ChevronDown
              size={16}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
          </div>
          {errors.ManagerId && (
            <p className="text-red-500 text-xs mt-1">{errors.ManagerId}</p>
          )}
        </div>


        {/* Time Zone */}
        <div className="col-span-1">
          <label className="block text-sm font-normal text-black mb-2">
            Time Zone
          </label>
          <div className="relative">
            <select
              name="timeZone"
              value={formData.timeZone}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 appearance-none bg-white"
            >
              <option value="">Select Time Zone</option>
              {[
                { label: "Asia/Kolkata (IST)", value: "Asia/Kolkata" },
                { label: "America/Chicago (CST)", value: "America/Chicago" },
                { label: "America/New_York (EST)", value: "America/New_York" },
                {
                  label: "America/Los_Angeles (PST)",
                  value: "America/Los_Angeles",
                },
                { label: "Europe/London (GMT)", value: "Europe/London" },
                { label: "Asia/Dubai (GST)", value: "Asia/Dubai" },
                { label: "Asia/Singapore (SGT)", value: "Asia/Singapore" },
                { label: "Australia/Sydney (AEST)", value: "Australia/Sydney" },
              ].map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
            <ChevronDown
              size={16}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
          </div>
        </div>

        {/* Upload Profile Picture */}
        {
          !hasDisabledFieldForUpdating && (
            <div className="col-span-1">
              <label className="block text-sm font-normal text-black mb-2">
                Upload Profile Picture (Optional)
              </label>
              <div className="flex items-center gap-3">
                <label className={`px-4 py-2 border border-gray-300 rounded-lg text-sm cursor-pointer hover:bg-gray-50 transition-colors flex items-center gap-2 ${(formData?.file && formData.file instanceof File) ? "w-full" : ""}`}>
                  <input
                    type="file"
                    name="file"
                    accept="image/*"
                    onChange={handleChange}
                    className="hidden"
                  />
                  <span>
                    <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17 8 12 3 7 8"></polyline>
                      <line x1="12" y1="3" x2="12" y2="15"></line>
                    </svg>
                  </span>
                  <span>Choose File</span>
                </label>
                {formData.file instanceof File ? (
                  <span 
                  className="text-sm text-gray-600 w-full"
                  style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}
                  >
                    {formData.file.name}
                  </span>
                ) : (
                  <span className="text-sm text-gray-400">No file chosen</span>
                )}
              </div>
              {errors.file && (
                <p className="text-red-500 text-xs mt-1">{errors.file}</p>
              )}
        </div>
          )
        }
      </div>
      {/* Action Buttons */}
      <div className="flex justify-end gap-3 mt-6">
        <button
          type="button"
          onClick={handleCancel}
          className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          className={`px-6 py-2.5 text-sm font-medium rounded-lg transition-colors ${
            hasChanges 
              ? "bg-[#7E57C2] text-white hover:bg-[#6D4CAB]" 
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
          disabled={!hasChanges}
        >
          Save
        </button>
      </div>
    </form>
  );
};

export default FormDesign;